import "../../../css/EventMappings.css";
import React, { useState, useEffect, useCallback } from "react";
import { Tabs, Table, Button, Checkbox, Tag, Dropdown, Modal, message } from "antd";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import {
  getEventMappings,
  getEnabledEventMappings,
  deleteEventMapping,
  updateEventMapping,
} from "../../../services/integrations/klaviyoEventMappingsService";
import EventMappingDrawer from "./EventMappingDrawer";
import AttributeMappingsTab from "./AttributeMappingsTab";

export default function EventMappingsTab({ subscriptionId }) {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnabledOnly, setShowEnabledOnly] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const res = showEnabledOnly
        ? await getEnabledEventMappings()
        : await getEventMappings();
      // getEventMappings returns the beta API's paged envelope ({content, page});
      // getEnabledEventMappings already unwraps to a plain array client-side.
      setMappings(Array.isArray(res) ? res : res?.content || []);
    } catch (err) {
      message.error("Failed to load event mappings");
      setMappings([]);
    }
    setLoading(false);
  }, [showEnabledOnly]);

  useEffect(() => {
    if (subscriptionId) {
      fetchMappings();
    }
  }, [subscriptionId, fetchMappings]);

  const handleEdit = (mapping) => {
    setEditMapping(mapping);
    setDrawerVisible(true);
  };

  const handleAdd = () => {
    setEditMapping(null);
    setDrawerVisible(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEventMapping(deleteTarget.event_mapping_id);
      message.success("Event mapping deleted");
      setDeleteTarget(null);
      fetchMappings();
    } catch (err) {
      message.error("Failed to delete event mapping");
    }
  };

  const handleToggleEnabled = async (mapping) => {
    try {
      // PATCH is a partial update -- only send the field that changed.
      await updateEventMapping(mapping.event_mapping_id, { enabled: !mapping.enabled });
      message.success(mapping.enabled ? "Mapping disabled" : "Mapping enabled");
      fetchMappings();
    } catch (err) {
      message.error("Failed to update mapping");
    }
  };

  const columns = [
    {
      title: "Polly Event",
      dataIndex: "bluestream_event_type",
      key: "bluestream_event_type",
    },
    {
      title: "Steps",
      key: "steps",
      render: (_, record) => record.steps?.length ?? 0,
      width: 80,
    },
    {
      title: "Primary Action",
      dataIndex: "vendor_action_name",
      key: "vendor_action_name",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) =>
        record.enabled ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Disabled</Tag>
        ),
    },
    {
      title: "Last Fired",
      key: "lastFired",
      width: 120,
      render: () => <span style={{ color: "#bbb" }}>never</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <a onClick={() => handleEdit(record)}>Edit</a>
          <Dropdown
            menu={{
              items: [
                {
                  key: "toggle",
                  label: record.enabled ? "Disable" : "Enable",
                  onClick: () => handleToggleEnabled(record),
                },
                {
                  key: "delete",
                  label: "Delete",
                  danger: true,
                  onClick: () => setDeleteTarget(record),
                },
              ],
            }}
            trigger={["click"]}
          >
            <MoreOutlined style={{ cursor: "pointer", fontSize: 16 }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  const innerTabItems = [
    {
      key: "event-mappings",
      label: "Event Mappings",
      children: loading ? null : mappings.length === 0 && !showEnabledOnly ? (
        <div className="event-mappings-empty">
          <h3>No event mappings configured.</h3>
          <p>
            When Polly fires events, nothing will reach Klaviyo until you add a mapping.
          </p>
          <div className="empty-actions">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="standard-button primary-orange-button"
              onClick={handleAdd}
            >
              Add Event Mapping
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="event-mappings-toolbar">
            <div className="toolbar-left">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="standard-button primary-orange-button"
                onClick={handleAdd}
              >
                Add Event Mapping
              </Button>
              <Checkbox
                checked={showEnabledOnly}
                onChange={(e) => setShowEnabledOnly(e.target.checked)}
              >
                Show enabled only
              </Checkbox>
            </div>
          </div>
          <Table
            dataSource={mappings}
            columns={columns}
            rowKey="event_mapping_id"
            loading={loading}
            pagination={false}
            size="middle"
          />
        </>
      ),
    },
    {
      key: "attribute-mappings",
      label: "Attribute Mappings",
      children: <AttributeMappingsTab subscriptionId={subscriptionId} />,
    },
  ];

  return (
    <div className="event-mappings-tab" style={{ marginTop: 8 }}>
      <Tabs items={innerTabItems} tabBarStyle={{ marginBottom: 20 }} />

      <EventMappingDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        onSaved={fetchMappings}
        editMapping={editMapping}
      />

      {/*
        ch60703: the starter-pack entry point is removed here because apply-template
        never had a backend (Task 5). StarterPackModal.js is left in the tree --
        the human partner wants the feature back once a real endpoint exists.
      */}

      <Modal
        title="Delete Event Mapping"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete the mapping for{" "}
          <strong>{deleteTarget?.bluestream_event_type}</strong>? This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
